import { useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

export function RoiCell({ data, trackName, tipLine, displayMode = 'roi', minWinRate = 0, maxWinRate = 0 }) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { bets = 0, profits = 0, profit = 0, returns = 0, roi = 0, wins = 0, medianReturn = 0 } = data || {};
  
  // Handle both 'profits' and 'profit' property names
  const actualProfits = profits || profit || 0;
  
  const formatNumber = (num) => num?.toLocaleString() || '0';
  const formatCurrency = (num) => (num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
  
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={isTooltipOpen} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger asChild>
          <div 
            className={`roi-cell-container relative ${displayMode === 'both' ? 'min-h-32 py-6 px-2' : 'h-20 p-2'} flex items-center justify-center cursor-pointer transition-all duration-200 ${
              roi > 0 ? 'bg-green-50' : roi < 0 ? 'bg-red-50' : ''
            } ${isTooltipOpen ? 'ring-4 ring-[#F30099] ring-opacity-60' : 'hover:ring-2 hover:ring-[#F30099] hover:ring-opacity-40'}`}
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            {/* Top left - Tips */}
            <div className="absolute top-1 left-1 text-[10px] text-gray-600">
              T: {formatNumber(bets)}
            </div>
            
            {/* Top right - Wins */}
            <div className="absolute top-1 right-1 text-[10px] text-gray-600">
              W: {formatNumber(wins)}
            </div>
            
            {/* Center - ROI, Win Rate, or Both */}
            {displayMode === 'both' ? (
              <div className="flex flex-col items-center justify-center">
                {/* ROI */}
                <div className="text-xs text-gray-500 mb-1">ROI</div>
                <div className={`text-base font-semibold ${
                  roi > 0 ? 'text-green-600' : roi < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {roi.toFixed(1)}%
                </div>
                
                {/* Dividing line */}
                <div className="w-8 h-px bg-gray-300 my-1"></div>
                
                {/* Win Rate */}
                <div className={`text-base font-semibold ${
                  (() => {
                    const winRate = bets > 0 ? (wins / bets) * 100 : 0;
                    if (maxWinRate === minWinRate) return 'text-gray-600'; // All same value
                    if (winRate === maxWinRate) return 'text-green-600'; // Highest
                    if (winRate === minWinRate) return 'text-red-600'; // Lowest
                    // Interpolate between red and green based on position
                    const range = maxWinRate - minWinRate;
                    const position = (winRate - minWinRate) / range;
                    if (position > 0.5) return 'text-green-600';
                    if (position < 0.5) return 'text-red-600';
                    return 'text-gray-600';
                  })()
                }`}>
                  {bets > 0 ? ((wins / bets) * 100).toFixed(1) : '0'}%
                </div>
                <div className="text-xs text-gray-500 mt-1">Win Rate</div>
              </div>
            ) : (
              <div className={`text-lg font-semibold ${
                displayMode === 'roi' 
                  ? (roi > 0 ? 'text-green-600' : roi < 0 ? 'text-red-600' : 'text-gray-600')
                  : (() => {
                      const winRate = bets > 0 ? (wins / bets) * 100 : 0;
                      if (maxWinRate === minWinRate) return 'text-gray-600'; // All same value
                      if (winRate === maxWinRate) return 'text-green-600'; // Highest
                      if (winRate === minWinRate) return 'text-red-600'; // Lowest
                      // Interpolate between red and green based on position
                      const range = maxWinRate - minWinRate;
                      const position = (winRate - minWinRate) / range;
                      if (position > 0.5) return 'text-green-600';
                      if (position < 0.5) return 'text-red-600';
                      return 'text-gray-600';
                    })()
              }`}>
                {displayMode === 'roi' 
                  ? `${roi.toFixed(1)}%`
                  : `${bets > 0 ? ((wins / bets) * 100).toFixed(1) : '0'}%`
                }
              </div>
            )}
            
            {/* Bottom left - Returns */}
            <div className="absolute bottom-0.5 left-1 text-[10px] text-gray-600">
              R: ${formatCurrency(returns)}
            </div>
            
            {/* Bottom right - Profit */}
            <div className="absolute bottom-0.5 right-1 text-[10px] text-gray-600">
              P: ${formatCurrency(actualProfits)}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent 
          className="p-4 bg-white border border-gray-200 shadow-xl rounded-lg"
          side="top"
          align="center"
        >
          <div className="h-full flex flex-col justify-center space-y-3">
            {/* Track and Tip Line Info */}
            {(trackName || tipLine) && (
              <div className="text-center pb-2 border-b border-gray-200">
                {trackName && (
                  <div className="text-base font-semibold text-gray-800 mb-1">
                    {trackName}
                  </div>
                )}
                {tipLine && (
                  <div className="text-xs text-gray-600">
                    Tip Line: {tipLine}
                  </div>
                )}
              </div>
            )}
            
            {/* Header with ROI and Win Rate */}
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div className={`text-xl font-bold mb-1 ${
                  roi > 0 ? 'text-green-600' : roi < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {roi.toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500">ROI</div>
              </div>
              
              <div className="text-center">
                <div className={`text-xl font-bold mb-1 ${
                  (() => {
                    const winRate = bets > 0 ? (wins / bets) * 100 : 0;
                    if (maxWinRate === minWinRate) return 'text-gray-600'; // All same value
                    if (winRate === maxWinRate) return 'text-green-600'; // Highest
                    if (winRate === minWinRate) return 'text-red-600'; // Lowest
                    // Interpolate between red and green based on position
                    const range = maxWinRate - minWinRate;
                    const position = (winRate - minWinRate) / range;
                    if (position > 0.5) return 'text-green-600';
                    if (position < 0.5) return 'text-red-600';
                    return 'text-gray-600';
                  })()
                }`}>
                  {bets > 0 ? ((wins / bets) * 100).toFixed(1) : '0'}%
                </div>
                <div className="text-xs text-gray-500">Win Rate</div>
              </div>
            </div>
            
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* Tips */}
              <div className="text-center p-2 bg-blue-50 rounded">
                <div className="text-lg font-bold text-blue-600">{formatNumber(bets)}</div>
                <div className="text-xs text-blue-700 font-medium">Tips</div>
              </div>
              
              {/* Wins */}
              <div className="text-center p-2 bg-green-50 rounded">
                <div className="text-lg font-bold text-green-600">{formatNumber(wins)}</div>
                <div className="text-xs text-green-700 font-medium">Wins</div>
              </div>
              
              {/* Returns */}
              <div className="text-center p-2 bg-purple-50 rounded">
                <div className="text-lg font-bold text-purple-600">${formatCurrency(returns)}</div>
                <div className="text-xs text-purple-700 font-medium">Returns</div>
              </div>
              
              {/* Profit */}
              <div className="text-center p-2 bg-orange-50 rounded">
                <div className={`text-lg font-bold ${actualProfits >= 0 ? 'text-orange-600' : 'text-red-600'}`}>
                  ${formatCurrency(actualProfits)}
                </div>
                <div className="text-xs text-orange-700 font-medium">Profit</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {/* Average Return */}
              <div className="text-center pt-1 border-t border-gray-200">
                <div className="text-base font-semibold text-gray-700">
                  ${wins > 0 ? formatCurrency(returns / wins) : '0'}
                </div>
                <div className="text-xs text-gray-500">Average Return</div>
              </div>
              
              {/* Median Return */}
              <div className="text-center pt-1 border-t border-gray-200">
                <div className="text-base font-semibold text-gray-700">
                  ${formatCurrency(medianReturn)}
                </div>
                <div className="text-xs text-gray-500">Median Return</div>
              </div>
            </div>
            
            {/* Return Distribution Explanation */}
            {wins > 0 && medianReturn > 0 && (
              <div className="mt-2 pt-1">
                <div className="text-xs text-gray-600 text-center max-w-[250px] mx-auto">
                  {(() => {
                    const avgReturn = returns / wins;
                    const ratio = avgReturn / medianReturn;
                    
                    if (ratio > 1.5) {
                      return "Average much higher than median suggests some big winning returns skew the average upward.";
                    } else if (ratio > 1.2) {
                      return "Average higher than median indicates occasional larger winning returns.";
                    } else if (ratio < 0.8) {
                      return "Average lower than median suggests more consistent, smaller winning returns.";
                    } else if (ratio < 0.5) {
                      return "Average much lower than median indicates very consistent small winning returns.";
                    } else {
                      return "Average and median are similar, indicating consistent winning return amounts.";
                    }
                  })()}
                </div>
              </div>
            )}

          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
